import { useState, useEffect, useCallback, useMemo } from 'react';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useDanelColumns } from '../../../pages/DanelPage/hooks/useDanelColumns';
import { DataFeedRowData } from '../../../types/generatedTypes';
import { useDataFeedWizardStore } from '../../../store/dataFeedWizard/dataFeedWizardStore';
import Actions from '../../../store/Actions';
import { useDisabledRows } from '../hooks/useDisabledRows';
import { useLinkedAccount } from '../hooks/useLinkedAccount';
import { useProcessedStatus } from '../hooks/useProcessedStatus';
import { useTableSearch } from '../../../hooks/useTableSearch';

export const useShowFeedModal = (filterBy?: { symbol?: string | null; isin?: string | null }) => {
  const { sessionId, fileId } = useDataFeedWizardStore();
  const { isRowDisabled } = useDisabledRows();
  const linkedAccountData = useLinkedAccount();
  const [rawRows, setRawRows] = useState<DataFeedRowData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [autosizeColumns, setAutosizeColumns] = useState(false);
  const apiRef = useGridApiRef();

  // Use the processed status hook to merge upload_status
  const { mergedRows } = useProcessedStatus(fileId, rawRows);

  // Pre-filter rows by symbol/ISIN when filterBy is provided
  const preFilteredRows = useMemo(() => {
    if (!filterBy?.symbol && !filterBy?.isin) return mergedRows;

    return mergedRows.filter(row => {
      const cellValues = Object.values(row)
        .filter((v): v is string => typeof v === 'string')
        .map(v => v.toLowerCase());

      if (filterBy.symbol && cellValues.some(v => v === filterBy.symbol!.toLowerCase())) {
        return true;
      }
      if (filterBy.isin && cellValues.some(v => v === filterBy.isin!.toLowerCase())) {
        return true;
      }
      return false;
    });
  }, [mergedRows, filterBy?.symbol, filterBy?.isin]);

  const { searchTerm, handleSearch, filteredData } = useTableSearch(preFilteredRows, {
    debounceMs: 300,
  });

  const loadData = useCallback(async () => {
    if (!sessionId) {
      setRawRows([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await Actions.getWizardFileData(sessionId);
      // Add id to each row based on _row_index for consistency
      const rowsWithId = (response.rows || []).map(
        (row: DataFeedRowData, index: number) => ({
          ...row,
          id: String(row._row_index ?? index),
          _row_index: row._row_index ?? index,
        })
      );
      setRawRows(rowsWithId);
    } catch (error) {
      console.error('Failed to load wizard file data:', error);
      setRawRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Create a no-op processRowUpdate for read-only mode
  const noOpProcessRowUpdate = async (
    newRow: DataFeedRowData,
    _oldRow: DataFeedRowData
  ): Promise<DataFeedRowData> => {
    return newRow;
  };

  const { columns } = useDanelColumns({
    rows: filteredData,
    processRowUpdate: noOpProcessRowUpdate,
    autosizeColumns,
    viewMode: 'raw',
    appliedTemplateConfig: null,
    readOnly: true,
    hidePreviewColumns: true,
    linkedAccountData,
  });

  // Autosize columns when toggle is enabled and data is loaded
  useEffect(() => {
    if (
      autosizeColumns &&
      apiRef.current &&
      columns.length > 0 &&
      filteredData.length > 0
    ) {
      if (apiRef.current?.autosizeColumns) {
        const columnsToAutosize = columns.map(col => col.field);
        apiRef.current.autosizeColumns({
          includeHeaders: true,
          includeOutliers: true,
          expand: true,
          columns: columnsToAutosize,
        });
      }
    }
  }, [autosizeColumns, columns, filteredData, apiRef]);

  return {
    rows: filteredData,
    totalRowCount: preFilteredRows.length,
    columns,
    isLoading,
    autosizeColumns,
    setAutosizeColumns,
    apiRef,
    isRowDisabled,
    searchTerm,
    handleSearch,
  };
};
